import type { DragEvent } from 'react';
import { Download, Upload, FileSpreadsheet, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EntityImportConfig } from '../types';
import { importFieldLabel } from '../importI18n';

interface FileStepProps {
  config: EntityImportConfig;
  fileName: string | null;
  headers: string[];
  dragging: boolean;
  downloading: boolean;
  uomLabels?: string[];
  onDownloadTemplate: () => void;
  onFileSelected: (file: File) => void;
  onDragState: (dragging: boolean) => void;
}

export default function FileStep({
  config,
  fileName,
  headers,
  dragging,
  downloading,
  uomLabels = [],
  onDownloadTemplate,
  onFileSelected,
  onDragState,
}: FileStepProps) {
  const { t } = useTranslation();
  const entityKey = config.entityId;

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    onDragState(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  const hints = [
    ...(config.fileHints || []),
    ...config.fields
      .filter((f) => f.hint)
      .map((f) => `${importFieldLabel(t, f.fieldname, f.label)}: ${f.hint}`),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">
          {t(`import.${entityKey}.step1Title`, { defaultValue: 'Step 1: Select File' })}
        </h4>
        <p className="text-gray-600 text-sm">
          {t(`import.${entityKey}.step1Help`, {
            defaultValue: `Download the ${config.doctypeLabel} template, fill in your data, then upload a CSV or Excel (.xlsx) file.`,
          })}
        </p>
      </div>

      {(hints.length > 0 || (config.showUomGuide && uomLabels.length > 0)) && (
        <div className="rounded-lg border border-primary-100 bg-primary-50 p-4 text-sm text-primary-900">
          <div className="flex items-start gap-2 mb-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="font-semibold">{t('import.fieldGuide')}</p>
          </div>
          <ul className="list-disc pl-6 space-y-1 text-primary-800">
            {hints.map((h) => (
              <li key={h}>{h}</li>
            ))}
            {config.showUomGuide && uomLabels.length > 0 && (
              <li>
                <span className="font-medium">{t('import.availableUoms')}</span>{' '}
                {uomLabels.join(', ')}
              </li>
            )}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={onDownloadTemplate}
        disabled={downloading}
        className="btn-secondary inline-flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        {downloading ? t('import.downloading') : t('import.downloadTemplate')}
      </button>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          onDragState(true);
        }}
        onDragLeave={() => onDragState(false)}
        onDrop={handleDrop}
        className={`relative rounded-xl border-2 border-dashed p-10 text-center transition-colors cursor-pointer ${
          dragging
            ? 'border-primary-600 bg-primary-100'
            : 'border-primary-500 bg-primary-50 hover:bg-primary-100'
        }`}
      >
        <input
          type="file"
          accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelected(file);
            e.target.value = '';
          }}
        />
        <Upload className="w-10 h-10 text-primary-600 mx-auto mb-3" />
        <p className="font-medium text-gray-900">{t('import.dragDrop')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('import.orBrowse')}</p>
      </div>

      {fileName && (
        <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg">
          <FileSpreadsheet className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{fileName}</p>
            <p className="text-sm text-gray-500 mt-1">
              {t('import.detectedColumns')}{' '}
              {headers.length === 0
                ? '—'
                : headers.map((h) => (
                    <span
                      key={h}
                      className="inline-block mr-1 mb-1 px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-xs font-medium"
                    >
                      {h}
                    </span>
                  ))}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
